import { AndOperatorLabel } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview/AndOperatorLabel';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { WhereClauseField } from '@modules/common/components/WhereClauseField';
import {
  CombinedDashboardFilterField,
  CombinedQuantifyFilterField,
} from '@modules/quantify/components/QuantifyQueryBuilder/Fields/CombinedDashboadQuantifyFilterFields';
import { EditableCompareFieldName } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/EditableCompareFieldName/EditableCompareFieldName';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useGetQuantifyCompareQuery,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyQuery } from '@modules/quantify/store/types';
import {
  combineWhereClauseWithMetadataFilterIfRequired,
  isValidQuery,
  shouldUpdateCompareDenominatorOnFilterChange,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { RestartAltRounded } from '@mui/icons-material';
import { Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

const CompareFilterBContent = () => {
  const [shouldAddFromFilterA, setShouldAddFromFilterA] = useState(false);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);

  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const whereClauseA = useQuantifyStore(
    ({ query }) => query?.whereClause ?? EmptyWhereClause,
  );
  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause,
  );
  const primaryCompareQuery = useGetQuantifyCompareQuery({
    shouldIncludeDashboardFilters: false,
  });

  const whereClauseB = primaryCompareQuery?.whereClause
    ? primaryCompareQuery.whereClause
    : null;

  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );
  const query = useQuantifyStore(({ query }) => query);

  const qbDispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const nodesByID = useQueryBuilderStore(({ query }) => query.nodes);

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  useEffect(() => {
    dispatch({
      type: QuantifyActionType.SET_MISC,
      payload: {
        compareQBFieldB: {
          dispatch: qbDispatch,
          rootID,
          nodesByID,
        },
      },
    });

    return () => {
      dispatch({
        type: QuantifyActionType.SET_MISC,
        payload: {
          compareQBFieldB: null,
        },
      });
    };
  }, [dispatch, nodesByID, qbDispatch, rootID]);

  const onChange = useCallback(
    ({ whereClause }: { whereClause: WhereClause | null }) => {
      const queryPayload: Partial<QuantifyQuery> = {
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
        compareQuery: {
          ...query.compareQuery,
          whereClause: whereClause ?? undefined,
        },
      };

      if (!whereClause) {
        queryPayload.compareMetadata = {
          deltaQueryName: '',
        };
      }

      if (
        shouldUpdateCompareDenominatorOnFilterChange({
          groupBy: query.groupByField,
          compareWhereClause: whereClauseB,
          compareDenominatorWhereClause:
            primaryCompareQuery?.formula?.denominatorWhereClause ??
            EmptyWhereClause,
          formulaID,
        })
      ) {
        queryPayload.compareQuery = {
          ...queryPayload.compareQuery,
          formula: {
            ...query.compareQuery?.formula,
            denominatorWhereClause:
              combineWhereClauseWithMetadataFilterIfRequired({
                whereClause: whereClause ?? EmptyWhereClause,
                groupBy: query.groupByField,
              }),
          },
        };
      }

      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: queryPayload,
      });

      setShouldFetchReportData(true);
      trackQuantifyCompareEvents({
        eventName: 'Quantify Compare - Filter B Changed',
        link: analysisLink,
      });
    },
    [
      query.compareQuery,
      query.groupByField,
      whereClauseB,
      primaryCompareQuery?.formula?.denominatorWhereClause,
      formulaID,
      dispatch,
      analysisLink,
    ],
  );

  const onAddFromFilterAClick = () => {
    setShouldAddFromFilterA(true);
    setShouldOpenModal(true);
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Add From Filter A Button Clicked',
      link: analysisLink,
    });
  };

  const onReset = useCallback(() => {
    onChange({ whereClause: EmptyWhereClause });
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - Reset Filter Icon Button Clicked',
      link: analysisLink,
    });
  }, [analysisLink, onChange]);

  const onChangeFilterBName = useCallback(
    (filterBName: string) => {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          compareMetadata: {
            deltaQueryName: filterBName,
          },
        },
      });
      trackQuantifyCompareEvents({
        eventName: 'Quantify Compare - Filter B renamed',
        filterName: filterBName,
        link: analysisLink,
      });
    },
    [analysisLink, dispatch],
  );

  const isFilterBEmpty =
    !whereClauseB || isEqual(EmptyWhereClause, whereClauseB);

  const areBothFiltersEqual = isEqual(
    whereClauseB ?? EmptyWhereClause,
    whereClauseA,
  );

  const renderFilterBField = useCallback(() => {
    if (!dashboardWhereClause) {
      return (
        <WhereClauseField
          whereClause={shouldAddFromFilterA ? whereClauseA : whereClauseB}
          whereClauseForPreview={whereClauseB}
          onChange={onChange}
          modalTitle={`Add ${compareFilterBName}`}
          label={null}
          helperTextNode={
            isFilterBEmpty ? (
              <Typography variant="caption" color="info.main">
                Filter cannot be left empty
              </Typography>
            ) : null
          }
          shouldOpenModal={shouldOpenModal}
          onShouldOpenModelChange={(shouldOpen) => {
            setShouldOpenModal(shouldOpen);
            if (!shouldOpen && shouldAddFromFilterA) {
              setShouldAddFromFilterA(false);
            }
          }}
          forceEnableExecuteButton={Boolean(shouldAddFromFilterA)}
          modalTestId="quantify-compare-filter-field-b-modal"
        />
      );
    }

    return (
      <Stack>
        <CombinedDashboardFilterField
          onRemove={() => setShouldFetchReportData(true)}
        />
        <AndOperatorLabel />
        <CombinedQuantifyFilterField
          whereClause={shouldAddFromFilterA ? whereClauseA : whereClauseB}
          whereClauseForPreview={whereClauseB}
          onChange={onChange}
          modalTitle={`Add ${compareFilterBName}`}
          label={null}
          helperTextNode={
            isFilterBEmpty ? (
              <Typography variant="caption" color="info.main">
                Filter cannot be left empty
              </Typography>
            ) : null
          }
          shouldOpenModal={shouldOpenModal}
          onShouldOpenModelChange={(shouldOpen) => {
            setShouldOpenModal(shouldOpen);
            if (!shouldOpen && shouldAddFromFilterA) {
              setShouldAddFromFilterA(false);
            }
          }}
          forceEnableExecuteButton={Boolean(shouldAddFromFilterA)}
          modalTestId="quantify-compare-filter-field-b-modal"
          onClear={onReset}
        />
      </Stack>
    );
  }, [
    compareFilterBName,
    dashboardWhereClause,
    isFilterBEmpty,
    onChange,
    onReset,
    shouldAddFromFilterA,
    shouldOpenModal,
    whereClauseA,
    whereClauseB,
  ]);

  return (
    <Stack spacing={1} data-testid="quantify-compare-filter-field-b">
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <EditableCompareFieldName
          initialFieldName={compareFilterBName}
          onChange={onChangeFilterBName}
        />
        <Stack
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="end"
          flex={1}
        >
          <Button
            size="small"
            onClick={onAddFromFilterAClick}
            disabled={areBothFiltersEqual}
            sx={{
              maxWidth: 0.5,
            }}
          >
            Add From {DEFAULT_COMPARE_FILTER_A_NAME}
          </Button>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: '20px !important',
              alignSelf: 'center',
            }}
          />
          {!dashboardWhereClause && (
            <IconButton
              size="small"
              color="primary"
              disabled={isFilterBEmpty}
              onClick={onReset}
            >
              <RestartAltRounded fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {renderFilterBField()}
    </Stack>
  );
};

export const CompareFilterB = () => {
  return (
    <QueryBuilderStoreProvider>
      <CompareFilterBContent />
    </QueryBuilderStoreProvider>
  );
};
